import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Card,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftTypography from "components/SoftTypography";
import { useNavigate } from "react-router-dom";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { AxiosError } from "axios";
import { useSelector } from "react-redux";
import { RootState } from "store";

// Theme configuration
const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

interface AssetTypeItem {
  asset_type_id: number;
  asset_type_name: string;
}

interface Asset {
  asset_id: number;
}

// Component definitions - Already outside the parent component (GOOD!)
export const AssetNameCell = ({ value }: { value: string }) => {
  const assetName = value || "";
  const displayValue =
    assetName.length > 20 ? `${assetName.substring(0, 20)}...` : assetName;

  return (
    <Tooltip title={assetName}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

export const AssetStatusCell = ({ value }: { value: string }) => {
  if (!value || typeof value !== "string") return <span>-</span>;

  const formatted = value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

  return (
    <span>
      {formatted
        .replace("In Use No Subscription", "In Use (No Subscription)")
        .replace("Out Of Maintenance", "Out of Maintenance")
        .replace("Out Of Use", "Out of Use")
        .replace("Not Installed", "Not Installed")}
    </span>
  );
};

export const ActionsCell = ({
  data,
  handleView,
}: {
  data: Asset;
  handleView: (asset: Asset) => void;
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <IconButton aria-label="view" onClick={() => handleView(data)}>
        <VisibilityIcon />
      </IconButton>
    </Box>
  );
};

// Renderer helper functions - Outside the component
const renderAssetNameCell = (value: string) => <AssetNameCell value={value} />;

const renderAssetStatusCell = (value: string) => (
  <AssetStatusCell value={value} />
);

const renderActionsCell = (
  dataIndex: number,
  rows: Asset[],
  handleView: (asset: Asset) => void
) => <ActionsCell data={rows[dataIndex]} handleView={handleView} />;

const BranchHeadAssets = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<Asset[]>([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [assetTypes, setAssetTypes] = React.useState(null);
  const [assetTypeFilter, setAssetTypeFilter] =
    React.useState<AssetTypeItem | null>(null);

  const handleView = (asset: Asset) => {
    navigate(`/customer/assets-view/${asset.asset_id}`);
  };

  const columns: MUIDataTableColumn[] = React.useMemo(
    () => [
      { name: "asset_id", label: "Asset ID", options: { display: false } },
      { name: "division_name", label: "Division" },
      { name: "asset_service_id", label: "Asset Id" },
      { name: "asset_type_name", label: "Asset Type" },
      {
        name: "asset_name",
        label: "Brand Name",
        options: {
          filter: false,
          customBodyRender: renderAssetNameCell,
        },
      },
      {
        name: "asset_status",
        label: "Asset Status",
        options: {
          filter: true,
          sort: true,
          customBodyRender: renderAssetStatusCell,
        },
      },
      { name: "customer_name", label: "Customer" },
      { name: "asset_sku", label: "SKU" },
      {
        name: "Actions",
        label: "Actions",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) =>
            renderActionsCell(dataIndex, rows, handleView),
        },
      },
    ],
    [rows, handleView]
  );

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get(`/assets/branch-head/${user?.userId}`, {
        params: {
          filters: {
            asset_type_id: assetTypeFilter?.asset_type_id,
          },
        },
      });

      setRows(response?.data?.list);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
    }
  }, [assetTypeFilter, user?.userId]);

  React.useEffect(() => {
    fetchData();
  }, [assetTypeFilter, fetchData]);

  const fetchAssets = async () => {
    try {
      const assetTypesData = await axios.get("/asset-types");
      setAssetTypes(assetTypesData?.data?.list);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  React.useEffect(() => {
    fetchAssets();
  }, []);

  const fetchCustomersData = React.useMemo(
    () => async () => {
      try {
        await axios.get("/customer", {
          params: {
            is_active: 1,
          },
        });
      } catch (error) {
        if (error instanceof AxiosError && error) {
          setError(error);
        }
      }
    },
    []
  );

  React.useEffect(() => {
    fetchData();
    fetchCustomersData();
  }, [fetchData, fetchCustomersData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <FormControl sx={{ marginBottom: "3px" }}>
            <SoftTypography
              variant="caption"
              fontWeight="bold"
              sx={{ paddingBottom: "10px" }}
            >
              Asset Types
            </SoftTypography>
            <Autocomplete
              options={assetTypes ?? []}
              getOptionLabel={(option) => option.asset_type_name}
              value={assetTypeFilter}
              onChange={(event, value) => {
                setAssetTypeFilter(value);
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" />
              )}
            />
          </FormControl>
          <Box sx={{ display: "flex", gap: 2 }}></Box>
        </Grid>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-lifts"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Assets"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
    </DashboardLayout>
  );
};

export default BranchHeadAssets;
